import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getContentByType, getContentFromCache, getTranslation, ContentDocument } from '../api/contentApi';

const PortraitPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';

  const [portrait, setPortrait] = useState<ContentDocument | null>(() => {
    const cached = getContentFromCache('portrait');
    return cached?.[0] ?? null;
  });
  const [loading, setLoading] = useState(() => getContentFromCache('portrait') === null);
  const [error, setError] = useState('');

  useEffect(() => {
    getContentByType('portrait')
      .then((items) => {
        setPortrait(items.length > 0 ? items[0] : null);
      })
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  }, [t]);

  const content = portrait ? getTranslation(portrait, currentLang) : null;

  return (
    <div>
      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/portrait.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            <div>
              <h1>{content?.name ?? ''}</h1>
              {content?.title && <p>{content.title}</p>}
            </div>
            {portrait?.portraitImageUrl && (
              <img
                src={portrait.portraitImageUrl}
                alt={content?.name || 'Portrait'}
                className="hero-portrait"
              />
            )}
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="max-w-4xl mx-auto py-12 px-4">
        {loading && <p>{t('common.loading')}</p>}
        {error && <div className="error">{error}</div>}
        {!loading && !error && !content && <p>{t('common.error')}</p>}
        {content?.biography && (
          <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: content.biography }} />
        )}
      </section>

      {/* Areas of expertise */}
      {content?.areasOfExpertise && content.areasOfExpertise.length > 0 && (
        <section className="bg-gray-50 py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-serif font-bold text-primary-800 mb-6">
              {currentLang === 'de' ? 'Tätigkeitsschwerpunkte' : currentLang === 'fr' ? 'Domaines de compétence' : 'Areas of Expertise'}
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {content.areasOfExpertise.map((area, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-accent-500 mt-1">&#9632;</span>
                  <span className="text-gray-700">{area}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Languages */}
      {content?.languages && content.languages.length > 0 && (
        <section className="max-w-4xl mx-auto py-8 px-4">
          <h2 className="text-2xl font-serif font-bold text-primary-800 mb-4">
            {currentLang === 'de' ? 'Sprachen' : currentLang === 'fr' ? 'Langues' : 'Languages'}
          </h2>
          <p className="text-gray-700">{content.languages.join(' · ')}</p>
        </section>
      )}

      {/* CTA */}
      {content?.ctaText && (
        <section className="bg-primary-900 text-white py-12 px-4 text-center">
          <Link to={`/${currentLang}/contact`} className="btn text-lg px-8 py-3">
            {content.ctaText}
          </Link>
        </section>
      )}
    </div>
  );
};

export default PortraitPage;
